import { createSingleFilter, createMultipleFilter } from "app/utils/filters";

import { useFilterParams } from "../../routing/useFilterParams";

import type {
  Filter,
  Filters,
  GenericFilter,
  TagFilter,
} from "../../routing/useFilterParams";
import type { FilterElementInput } from "app/views/cloudops/resource-matches/__generated__/ResourceMatchesTableRefetchQuery.graphql";

const isTag = (filter: Filter): filter is TagFilter[] =>
  typeof filter[0] !== "string" && "tagValue" in filter[0];

export const isResourceTagFilter = (
  key: string,
  filter: Filter,
): filter is TagFilter[] => key === "resource-tag" && isTag(filter);

// The "binding" filter is surfaced to users as "Deployment" with just two
// options. "undeployed" folds in the backend's "missing" state so that, from a
// user's perspective, a policy is simply either deployed or not deployed.
function formatBindingOperand(
  value: GenericFilter[] | string[],
): FilterElementInput | undefined {
  const operands = value.flatMap((filter) => {
    const filterValue = typeof filter === "string" ? filter : filter.value;
    if (filterValue === "undeployed") {
      return [
        createSingleFilter("binding", "undeployed"),
        createSingleFilter("binding", "missing"),
      ];
    }
    return [createSingleFilter("binding", filterValue)];
  });

  return createMultipleFilter(operands, "OR");
}

// translate filter form key/value into a FilterElementInput operand
export function translateOperand(
  key: string,
  value: Filter,
): FilterElementInput | undefined {
  if (key === "binding") {
    return formatBindingOperand(value as GenericFilter[] | string[]);
  }
  if (isResourceTagFilter(key, value)) {
    if (value.length === 1) {
      return createSingleFilter(
        `resource-tag:${value[0].key}`,
        value[0].tagValue,
        value[0].operator,
      );
    }
    const tagOperands = value.map((tag) =>
      createSingleFilter(`resource-tag:${tag.key}`, tag.tagValue, tag.operator),
    );
    return createMultipleFilter(tagOperands, "AND");
  }
  // The backend matches account names as substrings, so a filter for the
  // account "Casino" would also return "Casino Development" and every other
  // name containing it. Match whole names instead; the picker lets users add
  // one value per account they want.
  if (key === "account-name") {
    return formatOperand(key, value, "equals");
  }
  return formatOperand(key, value);
}

function formatOperand(
  key: string,
  value: GenericFilter[] | string[],
  operator?: string,
): FilterElementInput | undefined {
  if (value.length === 1) {
    if (typeof value[0] === "string") {
      return createSingleFilter(key, value[0], operator);
    }
    return createSingleFilter(key, value[0].value, operator);
  }

  const operands = value.map((filter) =>
    createSingleFilter(
      key,
      typeof filter === "string" ? filter : filter.value,
      operator,
    ),
  );

  return createMultipleFilter(operands, "OR");
}

/**
 * Constructs a FilterElementInput from extra operands and filters from searchParams
 *
 * @param extraOperands - array of additional operands to include in FilterElementInput
 * @returns a FilterElementInput
 */
export function useFilterElement(
  extraOperands: FilterElementInput[] = [],
): FilterElementInput | null {
  const [filters] = useFilterParams();

  return concatFiltersWithExtraOperands(filters, extraOperands);
}

/**
 * `useFilterElement` for callers outside React — route loaders, which build
 * query variables before the page that would call the hook exists.
 *
 * Unparseable filters are treated as absent rather than thrown, so a mangled
 * URL still fails where it did before: on the page's own read of it.
 */
export function filterElementFromSearchParams(
  searchParams: URLSearchParams,
  extraOperands: FilterElementInput[] = [],
): FilterElementInput | null {
  const raw = searchParams.get("filters");
  let filters: Filters | null = null;

  if (raw) {
    try {
      filters = JSON.parse(raw);
    } catch {
      filters = null;
    }
  }

  return concatFiltersWithExtraOperands(filters, extraOperands);
}

export function concatFiltersWithExtraOperands(
  filters: Filters | null,
  extraOperands: FilterElementInput[] = [],
) {
  const operands = Object.entries(filters ?? {})
    .map(([key, value]) => translateOperand(key, value))
    .filter((operand): operand is FilterElementInput => operand !== undefined);

  const allOperands: FilterElementInput[] = [];

  if (operands.length) {
    const operandsFilter = createMultipleFilter(operands, "AND");
    if (operandsFilter) {
      allOperands.push(operandsFilter);
    }
  }

  if (extraOperands.length) {
    const extraOperandsFilter = createMultipleFilter(extraOperands, "AND");
    if (extraOperandsFilter) {
      allOperands.push(extraOperandsFilter);
    }
  }

  if (allOperands.length === 0) {
    return null;
  }

  return createMultipleFilter(allOperands, "AND") || null;
}
